import pinterest from './pinterest'
import reddit from './reddit'
import tiktok from './tiktok'
import bing from './bing'

const lastEventSKUs = {
  view: null,
}

const isDifferentSKU = (sku, event) => {
  if (!sku) return false // if no sku, don't track

  if (lastEventSKUs[event] !== sku) {
    lastEventSKUs[event] = sku
    return true
  }
  return false
}

export const getProductPayload = (customisedProduct) => ({
  sku: customisedProduct.sku,
  product_id: customisedProduct.sku,
  variant: customisedProduct.sku,
  category: customisedProduct.baseSKU,
  brand: 'SunGod',
  legacy_sku: customisedProduct.legacySKU,
  base_sku: customisedProduct.baseSKU,
  name: customisedProduct.name,
  conversion_kit_enabled: customisedProduct.conversionKitEnabled,
  frame_choice_sku: customisedProduct.frameChoice?.sku,
  frame_choice_name: customisedProduct.frameChoice?.plaintextName,
  lens_choice_sku: customisedProduct.lensChoice?.sku,
  lens_choice_name: customisedProduct.lensChoice?.plaintextName,
  frame_type: customisedProduct.frameType,
  is_limited_edition: customisedProduct.isLimitedEdition,
  is_prebuilt: customisedProduct.isPrebuiltVariant,
  prebuilt_sku: customisedProduct.isPrebuiltVariant && customisedProduct.matchingPrebuiltVariant.sku,
  customised: !!customisedProduct.state.customised,
  preorder: customisedProduct.preorderPartSelected,
  in_stock: customisedProduct.isInStock,
  lens_tech: customisedProduct.lensTech,
  price: customisedProduct.price,
  currency: customisedProduct.product.currency,
  url: customisedProduct.url,
  image_url: `https://dynamic.sungod.co/?sku=${customisedProduct.legacySKU}`,
  value: customisedProduct.price,
})

export const getFBProductPayload = (customisedProduct) => {
  return {
    currency: customisedProduct.product.currency,
    value: customisedProduct.price,
    content_name: customisedProduct.name,
    content_category: customisedProduct.baseSKU,
    content_ids: [customisedProduct.adFeedSKU],
    content_type: 'product',
    delivery_category: 'home_delivery',
    contents: [getFBProductContentPayload(customisedProduct)],
  }
}

export const getFBProductContentPayload = (customisedProduct) => {
  return {
    id: customisedProduct.adFeedSKU,
    content_name: customisedProduct.name,
    content_category: customisedProduct.baseSKU,
    item_price: customisedProduct.price,
    quantity: 1,
  }
}

export default (tracker, fbq, googleAds = null, ga4 = null) => ({
  async view(customisedProduct, options = {}) {
    // Don't track the same SKU twice in a row
    if (!isDifferentSKU(customisedProduct?.sku, 'view')) return

    if (customisedProduct.adFeedSKU) {
      fbq.track('ViewContent', getFBProductPayload(customisedProduct))
    }

    // Track with new dispatcher (includes GA4 automatically)
    await tracker.track('Product Viewed', {
      ...getProductPayload(customisedProduct),
      ...options,
    })

    // Track with TikTok and Bing
    tiktok.viewContent({
      sku: customisedProduct.sku,
      name: customisedProduct.name,
      category: customisedProduct.baseSKU,
      currency: customisedProduct.product.currency,
      value: customisedProduct.price,
    })

    bing.viewContent({
      sku: customisedProduct.sku,
      name: customisedProduct.name,
      category: customisedProduct.baseSKU,
      currency: customisedProduct.product.currency,
      value: customisedProduct.price,
    })

    // Track with Google Ads for remarketing
    googleAds?.viewContent({
      sku: customisedProduct.sku,
      name: customisedProduct.name,
      category: customisedProduct.baseSKU,
      currency: customisedProduct.product.currency,
      value: customisedProduct.price,
      content_name: customisedProduct.name,
      content_category: customisedProduct.baseSKU,
      id: customisedProduct.sku,
    })

    // Track with GA4 ecommerce event
    ga4?.viewItem({
      currency: customisedProduct.product.currency,
      value: customisedProduct.price,
      name: customisedProduct.name,
      category: customisedProduct.baseSKU,
      sku: customisedProduct.sku,
      id: customisedProduct.sku,
    })
  },

  async customise(customisedProduct) {
    // Don't track the same SKU twice in a row
    if (!isDifferentSKU(customisedProduct?.sku, 'customise')) return

    if (customisedProduct.adFeedSKU) {
      fbq.track('CustomizeProduct', getFBProductPayload(customisedProduct))
    }

    await tracker.track('Product Customised', getProductPayload(customisedProduct))
  },

  async save(customisedProduct, options = {}) {
    await tracker.track('Product Design Saved', {
      ...getProductPayload(customisedProduct),
      ...options,
    })
  },

  async openedCustomiserModal() {
    await tracker.track('Customiser Modal Opened')
  },

  async closedCustomiserModal() {
    await tracker.track('Customiser Modal Closed')
  },

  async closedCustomiserModalWithoutSaving() {
    await tracker.track('Customiser Modal Closed Without Saving')
  },

  async selectHeroDesign(customisedProduct, heroDesign, heroDesignIndex) {
    // Don't track the same SKU twice in a row
    if (!isDifferentSKU(customisedProduct?.sku, 'selectHeroDesign')) return

    await tracker.track('Product Hero Design Selected', {
      ...getProductPayload(customisedProduct),
      hero_design_index: heroDesignIndex,
      hero_design_sku: heroDesign.sku,
    })
  },

  async selectSavedDesign(customisedProduct, savedDesign, swatchIndex) {
    // Don't track the same SKU twice in a row
    if (!isDifferentSKU(customisedProduct?.sku, 'selectSavedDesign')) return

    await tracker.track('Product Saved Design Selected', {
      ...getProductPayload(customisedProduct),
      hero_design_index: swatchIndex,
      hero_design_sku: savedDesign.sku,
    })
  },

  async add(customisedProduct, { location, quantity = 1, order = 0 }) {
    // Don't track the same SKU twice in a row
    if (!isDifferentSKU(customisedProduct?.sku, 'add')) return

    if (customisedProduct.adFeedSKU) {
      fbq.track('AddToCart', getFBProductPayload(customisedProduct))
    }

    pinterest.addToCart(getFBProductPayload(customisedProduct))
    reddit.addToCart(getFBProductPayload(customisedProduct))

    // Track with TikTok and Bing
    tiktok.addToCart({
      sku: customisedProduct.sku,
      name: customisedProduct.name,
      category: customisedProduct.baseSKU,
      currency: customisedProduct.product.currency,
      value: customisedProduct.price,
    })

    bing.addToCart({
      sku: customisedProduct.sku,
      name: customisedProduct.name,
      category: customisedProduct.baseSKU,
      currency: customisedProduct.product.currency,
      value: customisedProduct.price,
    })

    // Track with Google Ads conversion
    googleAds?.addToCart({
      sku: customisedProduct.sku,
      name: customisedProduct.name,
      category: customisedProduct.baseSKU,
      currency: customisedProduct.product.currency,
      value: customisedProduct.price,
      content_name: customisedProduct.name,
      content_category: customisedProduct.baseSKU,
      id: customisedProduct.sku,
      quantity,
    })

    // Track with GA4 ecommerce event
    ga4?.addToCart({
      currency: customisedProduct.product.currency,
      value: customisedProduct.price,
      name: customisedProduct.name,
      category: customisedProduct.baseSKU,
      sku: customisedProduct.sku,
      id: customisedProduct.sku,
      quantity,
    })

    await tracker.track('Product Added', {
      ...getProductPayload(customisedProduct),
      quantity,
      location,
      order,
    })
  },

  async remove(customisedProduct) {
    // Don't track the same SKU twice in a row
    if (!isDifferentSKU(customisedProduct?.sku, 'remove')) return

    // Track with GA4 ecommerce event
    ga4?.removeFromCart({
      currency: customisedProduct.product.currency,
      value: customisedProduct.price,
      name: customisedProduct.name,
      category: customisedProduct.baseSKU,
      sku: customisedProduct.sku,
      id: customisedProduct.sku,
      quantity: 1,
    })

    await tracker.track('Product Removed', getProductPayload(customisedProduct))
  },

  async upgrade(customisedProduct) {
    await tracker.track('Product Upgraded', getProductPayload(customisedProduct))
  },

  async downgrade(customisedProduct) {
    await tracker.track('Product Downgraded', getProductPayload(customisedProduct))
  },

  async lensTechSelected(customisedProduct, lensTech, location = '') {
    // Don't track the same SKU and lens tech twice in a row
    if (!isDifferentSKU(`${customisedProduct?.sku}-${lensTech}`, 'lenstech')) return

    await tracker.track('Product Lens Tech Selected', {
      ...getProductPayload(customisedProduct),
      lens_tech: lensTech,
      ...(location ? { location } : {}),
    })
  },

  async frameTypeSelected(customisedProduct, frameType, ckEnabled) {
    // Don't track the same SKU and frame type twice in a row
    if (!isDifferentSKU(`${customisedProduct?.sku}-${frameType}-${ckEnabled}`, 'lenstech')) return

    await tracker.track('Product Frame Type Selected', {
      ...getProductPayload(customisedProduct),
      frame_type: frameType,
      conversion_kit_enabled: ckEnabled,
    })
  },

  async variantGroupSelected(customisedProduct, key, targetSKU) {
    await tracker.track('Product Variant Group Selected', {
      ...getProductPayload(customisedProduct),
      key,
      target_sku: targetSKU,
    })
  },

  async clicked8KOUpgrade(customisedProduct) {
    await tracker.track('Upgrade to 8KO Clicked', getProductPayload(customisedProduct))
  },

  async openedLensTechModal(customisedProduct) {
    await tracker.track('Opened Lens Tech Modal', getProductPayload(customisedProduct))
  },
})
